/**
 * Workflow execution list for monitoring all workflow runs
 */

import React from 'react';
import {
  Box,
  Typography,
  Table,
  Chip,
  IconButton,
  Sheet,
} from '@mui/joy';
import {
  Play as PlayArrow,
  CheckCircle,
  XCircle as Error,
  Warning,
  Clock as Schedule,
  Eye as Visibility,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';

import type { WorkflowExecution } from '../../types/workflows';

interface WorkflowExecutionListProps {
  executions: WorkflowExecution[];
  onViewDetails?: (execution: WorkflowExecution) => void;
}

export default function WorkflowExecutionList({
  executions,
  onViewDetails,
}: WorkflowExecutionListProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <PlayArrow size={18} />;
      case 'completed':
        return <CheckCircle size={18} />;
      case 'failed':
        return <Error size={18} />;
      case 'waiting':
        return <Schedule size={18} />;
      case 'user_intervention':
        return <Warning size={18} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'primary' | 'success' | 'danger' | 'warning' | 'neutral' => {
    switch (status) {
      case 'running':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'waiting':
      case 'user_intervention':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  if (executions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
          No workflow executions found
        </Typography>
      </Box>
    );
  }

  return (
    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
      <Table stripe="odd" hoverRow>
        <thead>
          <tr>
            <th style={{ width: '5%' }}>Status</th>
            <th style={{ width: '30%' }}>Automation</th>
            <th style={{ width: '15%' }}>Current Node</th>
            <th style={{ width: '15%' }}>Started</th>
            <th style={{ width: '15%' }}>Resume At</th>
            <th style={{ width: '15%' }}>Error</th>
            <th style={{ width: '5%' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {executions.map((execution) => (
            <tr key={execution.id}>
              <td>
                <Chip
                  startDecorator={getStatusIcon(execution.status)}
                  color={getStatusColor(execution.status)}
                  variant="soft"
                  size="sm"
                >
                  {execution.status.replace('_', ' ')}
                </Chip>
              </td>
              <td>
                <Typography level="body-sm" sx={{ fontWeight: 'md' }}>
                  {execution.automation_name}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  ID: {execution.id}
                </Typography>
              </td>
              <td>
                <Typography level="body-sm">{execution.current_node_type}</Typography>
              </td>
              <td>
                <Typography level="body-sm">
                  {formatDistanceToNow(new Date(execution.created_at), { addSuffix: true })}
                </Typography>
              </td>
              <td>
                {execution.resume_at ? (
                  <Typography level="body-sm">
                    {formatDistanceToNow(new Date(execution.resume_at), { addSuffix: true })}
                  </Typography>
                ) : (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    -
                  </Typography>
                )}
              </td>
              <td>
                {execution.error_message ? (
                  <Typography
                    level="body-xs"
                    sx={{
                      color: 'danger.500',
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {execution.error_message}
                  </Typography>
                ) : (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    -
                  </Typography>
                )}
              </td>
              <td>
                {onViewDetails && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => onViewDetails(execution)}
                  >
                    <Visibility />
                  </IconButton>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}
